/** The filters over a list of clauses, shared by a document's Checklist tab and each
 * specialist's "Documented obligations" card: one dropdown per dimension (status, specialist,
 * topic, goal or section, document, kind) and a search over reference, title and text. */
import { useMemo } from "react";

import type { RegisterLine } from "../api";
import type { FilterDim, FilterState } from "./clauseFilterModel";
import styles from "./Clauses.module.css";
import { MultiSelectFilter } from "./MultiSelectFilter";

export const ClauseFilterBar = <L extends RegisterLine>({
  lines,
  dims,
  state,
  onChange,
  shown,
}: {
  /** Every clause the filters choose among, so only values one holds are offered. */
  lines: L[];
  dims: FilterDim<L>[];
  state: FilterState;
  onChange: (next: FilterState) => void;
  shown: number;
}) => {
  const held = useMemo(
    () =>
      Object.fromEntries(
        dims.map((dim) => [dim.key, new Set(lines.flatMap((l) => dim.values(l)))]),
      ),
    [lines, dims],
  );
  return (
    <div className={styles.filters} data-review-chrome>
      {dims.map((dim) => {
        const chosen = state.selected[dim.key] ?? [];
        const present = [...held[dim.key]];
        const order = dim.order ?? [...present].sort((a, b) => dim.labelOf(a).localeCompare(dim.labelOf(b)));
        const options = [
          ...order.filter((v) => held[dim.key].has(v) || chosen.includes(v)),
          ...present.filter((v) => !order.includes(v)),
        ];
        if (options.length < 2 && chosen.length === 0) return null;
        return (
          <MultiSelectFilter
            key={dim.key}
            options={options}
            selected={chosen}
            onChange={(next) =>
              onChange({ ...state, selected: { ...state.selected, [dim.key]: next } })
            }
            noun={dim.noun}
            labelOf={dim.labelOf}
            searchable={options.length > 8}
            size="compact"
          />
        );
      })}
      <input
        type="search"
        aria-label="Search the clauses"
        placeholder="Search the clauses"
        value={state.search}
        onChange={(e) => onChange({ ...state, search: e.target.value })}
      />
      <span className={styles.coverage}>
        {shown} of {lines.length} shown
      </span>
    </div>
  );
};
